import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import type { AdminMetricsResponse, CanaryStatus, CostPeriodKey, LambdaMetrics } from '../../services/api';
import { RefreshCw, Loader2, DollarSign, Users, Database, HardDrive, TrendingUp, Activity, ChevronRight, ExternalLink, Zap } from 'lucide-react';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { awsConsoleUrl, type AwsConsoleResource } from '../../utils/awsConsole';

const CANARY_DOT: Record<CanaryStatus, string> = {
  green: 'bg-green-500', amber: 'bg-amber-500', red: 'bg-red-500', info: 'bg-gray-400',
};
const fmtPct = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : `${(n * 100).toFixed(1)}%`;
const fmtMoney = (n: number | null | undefined) =>
  n === null || n === undefined || Number.isNaN(n) ? '—' : `$${n.toFixed(2)}`;
/** Human-readable size (binary units): 96076156 -> "91.6 MB". */
function fmtBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || bytes < 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${i === 0 ? v : v.toFixed(1)} ${units[i]}`;
}
const BUCKET_LABELS: Record<string, string> = {
  bedrock: 'Bedrock (AI)', dynamodb: 'DynamoDB', s3: 'S3', lambda: 'Lambda',
  api_gateway: 'API Gateway', cloudfront: 'CloudFront', cognito: 'Cognito',
  kms: 'KMS', route53: 'Route 53', other: 'Other',
};
const PERIOD_ORDER: CostPeriodKey[] = ['month_to_date', 'last_month', 'trailing_30d'];

export function MetricsDashboard() {
  const [metrics, setMetrics] = useState<AdminMetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [costPeriod, setCostPeriod] = useState<CostPeriodKey>('last_month');

  const loadMetrics = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getAdminMetrics();
      setMetrics(data);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadMetrics(); }, []);
  useAutoRefresh(loadMetrics);

  if (loading && !metrics) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Business Metrics</h1>
        <button
          onClick={loadMetrics}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg">{error}</div>
      )}

      {metrics && (
        <>
          {/* Key Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <MetricCard
              icon={<Users className="w-6 h-6 text-blue-500" />}
              label="Total Users"
              value={metrics.users?.total || 0}
              sub={`+${metrics.users?.new_users_7d || 0} this week`}
            />
            <RevenueCard metrics={metrics} />
            <MetricCard
              icon={<TrendingUp className="w-6 h-6 text-purple-500" />}
              label="Total Jobs"
              value={metrics.growth?.total_jobs || 0}
              sub={`+${metrics.growth?.new_jobs_7d || 0} this week`}
            />
            <MetricCard
              icon={<Database className="w-6 h-6 text-orange-500" />}
              label="Total Resumes"
              value={metrics.growth?.total_resumes || 0}
            />
          </div>

          {/* Canary Health Summary */}
          {metrics.canaries && (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  System Health — Canaries
                  <span className={`w-2.5 h-2.5 rounded-full ${CANARY_DOT[metrics.canaries.overall_status]}`} />
                </h2>
                <Link
                  to="/admin/f47ac10b58cc/canary-analytics"
                  className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Full report <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                <CanaryTile
                  label="Ingest Health"
                  status={metrics.canaries.ingest.status}
                  value={fmtPct(metrics.canaries.ingest.ingest_success_rate)}
                  hint="URL → real card"
                />
                <CanaryTile
                  label="Outcome Funnel"
                  status={metrics.canaries.outcomes.status}
                  value={`${metrics.canaries.outcomes.applied.toLocaleString()} applied`}
                  hint={`${fmtPct(metrics.canaries.outcomes.error_rate)} gen errors`}
                />
                <CanaryTile
                  label="Unit Economics"
                  status={metrics.canaries.economics.status}
                  value={metrics.canaries.economics.cost_per_active_user === null
                    ? '—' : `$${metrics.canaries.economics.cost_per_active_user.toFixed(2)}`}
                  hint="cost / active user"
                />
                <CanaryTile
                  label="Conversion"
                  status={metrics.canaries.conversion.status}
                  value={fmtPct(metrics.canaries.conversion.paid_conversion_rate)}
                  hint={`${metrics.canaries.conversion.paid_users.toLocaleString()} paid`}
                />
                <CanaryTile
                  label="Trust & Errors"
                  status={metrics.canaries.trust.status}
                  value={`${metrics.canaries.trust.open_errors.toLocaleString()} open`}
                  hint={metrics.canaries.trust.fabrication_signals
                    ? `${metrics.canaries.trust.fabrication_signals} fabrication ⚠`
                    : `${metrics.canaries.trust.errors_7d} in 7d`}
                />
              </div>
            </div>
          )}

          {/* User Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Users by Role</h2>
              <div className="space-y-2">
                {Object.entries(metrics.users?.by_role || {}).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between">
                    <span className="capitalize text-gray-600">{type}</span>
                    <span className="font-medium">{count as number}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Users by Status</h2>
              <div className="space-y-2">
                {Object.entries(metrics.users?.by_status || {}).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between">
                    <span className="capitalize text-gray-600">{status}</span>
                    <span className="font-medium">{count as number}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DynamoDBSection tables={metrics.dynamodb || {}} />
          <S3Section buckets={metrics.s3 || {}} />
          <LambdaSection lambda={metrics.lambda} />
          <CostSection
            cost={metrics.cost_estimate}
            nonTestUsers={metrics.users?.non_test_users}
            period={costPeriod}
            onPeriod={setCostPeriod}
          />
        </>
      )}
    </div>
  );
}

function CanaryTile({ label, status, value, hint }: { label: string; status: CanaryStatus; value: string; hint?: string }) {
  return (
    <div className="p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-1.5 mb-1">
        <span className={`w-2 h-2 rounded-full ${CANARY_DOT[status]}`} />
        <span className="text-xs text-gray-500">{label}</span>
      </div>
      <div className="text-lg font-bold text-gray-800">{value}</div>
      {hint && <div className="text-xs text-gray-400">{hint}</div>}
    </div>
  );
}

function MetricCard({ icon, label, value, sub, sub2, title }: {
  icon: React.ReactNode; label: string; value: string | number; sub?: string; sub2?: React.ReactNode; title?: string;
}) {
  return (
    <div className="bg-white rounded-lg shadow p-6" title={title}>
      <div className="flex items-center gap-3 mb-2">
        {icon}
        <span className="text-sm text-gray-500">{label}</span>
      </div>
      <div className="text-2xl font-bold text-gray-800">{value}</div>
      {sub && <div className="text-sm text-gray-500 mt-1">{sub}</div>}
      {sub2 && <div className="text-sm mt-0.5">{sub2}</div>}
    </div>
  );
}

function RevenueCard({ metrics }: { metrics: AdminMetricsResponse }) {
  const rev = metrics.revenue;
  const monthly = rev ? rev.monthly_revenue : null;
  let sub: string;
  if (monthly === null || monthly === undefined) {
    sub = rev?.note || rev?.error || 'Plan price unavailable (pricing_config)';
  } else {
    const unpriced = rev?.unpriced_paying_users ? ` (+${rev.unpriced_paying_users} unpriced)` : '';
    sub = `${rev?.paying_users ?? 0} paying × plan price${unpriced} · ${fmtMoney(rev?.annual_revenue_projected)}/yr`;
  }
  const margin = rev?.net_margin_monthly;
  const tierDetail = (rev?.by_tier || [])
    .map(t => `${t.tier}: ${t.users} × ${fmtMoney(t.monthly_price)}/mo = ${fmtMoney(t.monthly_revenue)}`)
    .join('\n');
  return (
    <MetricCard
      icon={<DollarSign className="w-6 h-6 text-green-500" />}
      label="Est. Monthly Revenue"
      value={fmtMoney(monthly)}
      sub={sub}
      sub2={margin === null || margin === undefined ? undefined : (
        <span className={margin >= 0 ? 'text-green-700' : 'text-red-600'}>
          Net {margin >= 0 ? '+' : '−'}${Math.abs(margin).toFixed(2)} vs {rev?.last_month_label || 'last month'} AWS ({fmtMoney(rev?.last_month_aws_cost)})
        </span>
      )}
      title={tierDetail || undefined}
    />
  );
}

/** An AWS object name linked to its console page (new tab). */
function ConsoleLink({ type, name, region }: { type: AwsConsoleResource; name: string; region?: string }) {
  return (
    <a
      href={awsConsoleUrl(type, name, region)}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 whitespace-nowrap text-primary-700 hover:text-primary-800 hover:underline"
      title={`Open ${name} in the AWS console`}
    >
      {name}
      <ExternalLink className="w-3 h-3 flex-shrink-0 opacity-60" aria-hidden="true" />
    </a>
  );
}

function DynamoDBSection({ tables }: { tables: AdminMetricsResponse['dynamodb'] }) {
  const rows = Object.entries(tables).sort(([, a], [, b]) => b.size_bytes - a.size_bytes);
  const ok = rows.filter(([, d]) => d.size_bytes >= 0);
  const totalItems = ok.reduce((n, [, d]) => n + d.item_count, 0);
  const totalBytes = ok.reduce((n, [, d]) => n + d.size_bytes, 0);
  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
        <Database className="w-5 h-5" /> DynamoDB Tables
        <span className="text-sm font-normal text-gray-500">({rows.length})</span>
      </h2>
      <p className="text-xs text-gray-500 mb-4">
        Every <code>skillsnap-*</code> table, largest first. Item counts and sizes are refreshed by AWS roughly every 6 hours.
      </p>
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 px-4 text-gray-600 font-medium">Table</th>
              <th className="text-right py-2 px-4 text-gray-600 font-medium">Items</th>
              <th className="text-right py-2 px-4 text-gray-600 font-medium">Size</th>
              <th className="text-center py-2 px-4 text-gray-600 font-medium">Billing</th>
              <th className="text-center py-2 px-4 text-gray-600 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([name, data]) => (
              <tr key={name} className="border-b hover:bg-gray-50">
                <td className="py-2 px-4 font-mono text-sm"><ConsoleLink type="dynamodb" name={name} /></td>
                <td className="py-2 px-4 text-right">{data.item_count < 0 ? '—' : data.item_count.toLocaleString()}</td>
                <td className="py-2 px-4 text-right" title={data.size_bytes >= 0 ? `${data.size_bytes.toLocaleString()} bytes` : data.error}>
                  {fmtBytes(data.size_bytes)}
                </td>
                <td className="py-2 px-4 text-center text-xs text-gray-500">
                  {data.billing_mode === 'PAY_PER_REQUEST' ? 'On-demand' : data.billing_mode === 'PROVISIONED' ? 'Provisioned' : (data.billing_mode || '—')}
                </td>
                <td className="py-2 px-4 text-center">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    data.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`} title={data.error}>
                    {data.status}
                  </span>
                </td>
              </tr>
            ))}
            <tr className="bg-gray-50 font-semibold">
              <td className="py-2 px-4">Total ({ok.length} tables)</td>
              <td className="py-2 px-4 text-right">{totalItems.toLocaleString()}</td>
              <td className="py-2 px-4 text-right">{fmtBytes(totalBytes)}</td>
              <td className="py-2 px-4" colSpan={2} />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function S3Section({ buckets }: { buckets: AdminMetricsResponse['s3'] }) {
  const rows = Object.entries(buckets).sort(([, a], [, b]) => b.size_bytes - a.size_bytes);
  const ok = rows.filter(([, d]) => d.size_bytes >= 0);
  const totalObjects = ok.reduce((n, [, d]) => n + d.object_count, 0);
  const totalBytes = ok.reduce((n, [, d]) => n + d.size_bytes, 0);
  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
        <HardDrive className="w-5 h-5" /> S3 Buckets
        <span className="text-sm font-normal text-gray-500">({rows.length})</span>
      </h2>
      <p className="text-xs text-gray-500 mb-4">
        Every <code>skillsnap-*</code> bucket plus the CDK asset bucket, largest first. S3 storage metrics are reported daily (latest datapoint shown).
      </p>
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 px-4 text-gray-600 font-medium">Bucket</th>
              <th className="text-right py-2 px-4 text-gray-600 font-medium">Objects</th>
              <th className="text-right py-2 px-4 text-gray-600 font-medium">Size</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([name, data]) => (
              <tr key={name} className="border-b hover:bg-gray-50">
                <td className="py-2 px-4 font-mono text-sm"><ConsoleLink type="s3" name={name} region={data.region} /></td>
                <td className="py-2 px-4 text-right">{data.object_count < 0 ? '—' : data.object_count.toLocaleString()}</td>
                <td className="py-2 px-4 text-right"
                  title={data.size_bytes >= 0
                    ? `${data.size_bytes.toLocaleString()} bytes${data.storage_types?.length ? ` (${data.storage_types.join(', ')})` : ''}`
                    : data.error}>
                  {fmtBytes(data.size_bytes)}
                </td>
              </tr>
            ))}
            <tr className="bg-gray-50 font-semibold">
              <td className="py-2 px-4">Total ({ok.length} buckets)</td>
              <td className="py-2 px-4 text-right">{totalObjects.toLocaleString()}</td>
              <td className="py-2 px-4 text-right">{fmtBytes(totalBytes)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

const LAMBDA_PREVIEW_ROWS = 25;
const fmtInt = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : n.toLocaleString();
const fmtMs = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : n >= 1000 ? `${(n / 1000).toFixed(2)} s` : `${Math.round(n)} ms`;
/** "2026-09-25T03:13:54.000+0000" -> "2026-09-25 03:13" (UTC). */
const fmtModified = (s: string | undefined) => (s ? s.slice(0, 16).replace('T', ' ') : '—');

function LambdaSection({ lambda }: { lambda: LambdaMetrics | undefined }) {
  const [filter, setFilter] = useState('');
  const [showAll, setShowAll] = useState(false);
  const all = useMemo(() => lambda?.functions || [], [lambda]);
  const q = filter.trim().toLowerCase();
  const matched = useMemo(() => (q ? all.filter(f => f.name.toLowerCase().includes(q)) : all), [all, q]);
  // A filter always shows every match; otherwise preview the top N.
  const rows = q || showAll ? matched : matched.slice(0, LAMBDA_PREVIEW_ROWS);
  const totals = lambda?.totals;
  const th = 'py-2 px-3 text-gray-600 font-medium';

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6" data-testid="lambda-functions-section">
      <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
        <Zap className="w-5 h-5" /> Lambda Functions
        <span className="text-sm font-normal text-gray-500">({all.length})</span>
      </h2>
      <p className="text-xs text-gray-500 mb-4">
        Every <code>skillsnap-*</code> function, most-invoked (30 days) first. From CloudWatch daily sums
        {lambda?.window ? <> ending {fmtModified(lambda.window.end)} UTC</> : null}; avg duration is call-weighted.
      </p>

      {lambda?.error && (
        <div className="mb-4 p-3 bg-amber-50 text-amber-800 rounded-lg text-sm">{lambda.error}</div>
      )}
      {!lambda && (
        <div className="mb-4 p-3 bg-amber-50 text-amber-800 rounded-lg text-sm">Lambda metrics unavailable.</div>
      )}

      {all.length > 0 && (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <input
              type="search"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder="Filter functions…"
              aria-label="Filter Lambda functions"
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm w-64 max-w-full"
            />
            {q && <span className="text-sm text-gray-500">{matched.length} of {all.length} match</span>}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b">
                  <th className={`text-left ${th}`}>Function</th>
                  <th className={`text-right ${th}`}>Invocations 24h</th>
                  <th className={`text-right ${th}`}>Invocations 30d</th>
                  <th className={`text-right ${th}`}>Errors 30d</th>
                  <th className={`text-right ${th}`}>Avg duration 30d</th>
                  <th className={`text-right ${th}`}>Memory MB</th>
                  <th className={`text-left ${th}`}>Last modified (UTC)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(f => (
                  <tr key={f.name} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-3 font-mono text-sm"><ConsoleLink type="lambda" name={f.name} /></td>
                    <td className="py-2 px-3 text-right">{fmtInt(f.invocations_24h)}</td>
                    <td className="py-2 px-3 text-right">{fmtInt(f.invocations_30d)}</td>
                    <td className={`py-2 px-3 text-right ${f.errors_30d > 0 ? 'text-red-600 font-medium' : ''}`}>
                      {fmtInt(f.errors_30d)}
                    </td>
                    <td className="py-2 px-3 text-right">{fmtMs(f.avg_duration_ms_30d)}</td>
                    <td className="py-2 px-3 text-right" title={f.timeout_s ? `timeout ${f.timeout_s}s · ${f.runtime || ''}` : undefined}>
                      {fmtInt(f.memory_mb)}
                    </td>
                    <td className="py-2 px-3 text-sm text-gray-500 whitespace-nowrap">{fmtModified(f.last_modified)}</td>
                  </tr>
                ))}
                {totals && (
                  <tr className="bg-gray-50 font-semibold">
                    <td className="py-2 px-3">Total ({totals.functions} functions)</td>
                    <td className="py-2 px-3 text-right">{fmtInt(totals.invocations_24h)}</td>
                    <td className="py-2 px-3 text-right">{fmtInt(totals.invocations_30d)}</td>
                    <td className="py-2 px-3 text-right">{fmtInt(totals.errors_30d)}</td>
                    <td className="py-2 px-3 text-right">{fmtMs(totals.avg_duration_ms_30d)}</td>
                    <td className="py-2 px-3" colSpan={2} />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {!q && matched.length > LAMBDA_PREVIEW_ROWS && (
            <button
              type="button"
              onClick={() => setShowAll(v => !v)}
              className="mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              {showAll ? `Show top ${LAMBDA_PREVIEW_ROWS}` : `Show all ${matched.length}`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

function CostSection({ cost, nonTestUsers, period, onPeriod }: {
  cost: AdminMetricsResponse['cost_estimate'] | undefined;
  nonTestUsers: number | undefined;
  period: CostPeriodKey;
  onPeriod: (p: CostPeriodKey) => void;
}) {
  const periods = cost?.periods || {};
  const order = (cost?.period_order || PERIOD_ORDER).filter(k => periods[k]);
  const denom = cost?.non_test_users ?? nonTestUsers;
  const selectedKey = periods[period] ? period : order[0];
  const selected = selectedKey ? periods[selectedKey] : undefined;
  const buckets = cost?.buckets || Object.keys(selected?.by_bucket || {});
  const unavailable = !cost || cost.source !== 'cost_explorer';

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
        <DollarSign className="w-5 h-5" /> AWS Cost
      </h2>
      <p className="text-xs text-gray-500 mb-4">
        Actual spend from AWS Cost Explorer (UnblendedCost — tax and credits included).
        {denom ? <> Per-user figures ÷ <span className="font-medium text-gray-700">{denom} non-test users</span> (excludes test, qa, admin, snappy roles).</> : null}
      </p>

      {unavailable && (
        <div className="mb-4 p-3 bg-amber-50 text-amber-800 rounded-lg text-sm">
          Cost Explorer unavailable{cost?.error ? `: ${cost.error}` : ''} — no cost figures shown.
        </div>
      )}

      {order.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          {order.map(key => {
            const p = periods[key]!;
            const active = key === selectedKey;
            return (
              <button
                key={key}
                type="button"
                onClick={() => onPeriod(key)}
                className={`text-left p-4 rounded-lg border transition-colors ${
                  active ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                }`}
              >
                <div className="text-sm font-medium text-gray-700">{p.label}</div>
                <div className="text-xs text-gray-400">{p.start} → {p.end_inclusive} · {p.days}d</div>
                <div className="mt-1 text-2xl font-bold text-gray-800">{fmtMoney(p.total)}</div>
                <div className="text-sm text-gray-600">
                  {p.per_user_total === null ? '— / user' : `${fmtMoney(p.per_user_total)} / user`}
                  {denom ? <span className="text-gray-400"> (÷ {denom})</span> : null}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selected && (
        <>
          <div className="text-sm text-gray-500 mb-2">Breakdown — {selected.label}</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {buckets.map(b => {
              const amount = selected.by_bucket[b] ?? 0;
              const perUser = selected.per_user_by_bucket?.[b];
              const services = selected.by_service.filter(s => s.bucket === b);
              return (
                <div
                  key={b}
                  className={`text-center p-3 rounded-lg ${amount > 0 ? 'bg-gray-50' : 'bg-gray-50 opacity-60'}`}
                  title={services.length ? services.map(s => `${s.service}: $${s.amount.toFixed(2)}`).join('\n') : undefined}
                >
                  <div className="text-sm text-gray-500">{BUCKET_LABELS[b] || b}</div>
                  <div className="text-lg font-semibold">{fmtMoney(amount)}</div>
                  <div className="text-xs text-gray-400">
                    {perUser === null || perUser === undefined ? '—' : `${fmtMoney(perUser)} / user`}
                  </div>
                </div>
              );
            })}
          </div>
          {selected.by_service.length > 0 && (
            <details className="mt-4">
              <summary className="text-sm text-primary-600 cursor-pointer hover:text-primary-700">
                Service detail ({selected.by_service.length} services)
              </summary>
              <table className="min-w-full mt-2 text-sm">
                <tbody>
                  {selected.by_service.map(s => (
                    <tr key={s.service} className="border-b">
                      <td className="py-1 px-4">{s.service}</td>
                      <td className="py-1 px-4 text-gray-400">{BUCKET_LABELS[s.bucket] || s.bucket}</td>
                      <td className="py-1 px-4 text-right">{fmtMoney(s.amount)}</td>
                      <td className="py-1 px-4 text-right text-gray-400">
                        {denom ? `${fmtMoney(s.amount / denom)} / user` : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
          <div className="mt-4 pt-4 border-t text-right">
            <span className="text-gray-600">Total — {selected.label}: </span>
            <span className="text-xl font-bold">{fmtMoney(selected.total)}</span>
            {selected.per_user_total !== null && (
              <span className="text-gray-500"> · {fmtMoney(selected.per_user_total)} per non-test user</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
